import React from 'react';
import { Heart, Trash } from '@phosphor-icons/react';

import {
  Button,
  IconButton,
  Pagination,
  Pill,
  Skeleton,
  Table,
  TableThumb,
  Toolbar,
  ViewToggle,
} from '../../../design-system/components/desktop';
import { Icon } from '../../../design-system/components/Icon';
import { ListingCard, ListingGrid } from '../../../design-system/patterns/market';
import { ConsoleEmpty, ConsoleError } from '../../../design-system/patterns/console';
import { useViewport } from '../../../design-system/hooks/useViewport';
import { formatCurrency, mediaUrl } from '../../../utils/common';
import { routes } from '../../../routes/routes';

import '../../Dashboard/dashboard.css';

/**
 * The buyer's collection, the view. Pure.
 *
 * A COLLECTION IS OWNED, SO IT DEFAULTS TO A TABLE. This is the one call in
 * this batch that could have gone either way, since a saved card is also
 * something you desire. The table is the default because of what the screen is
 * FOR: finding the card you saved, seeing what it is worth now, and either
 * going to it or dropping it. That is managing. The marketplace is where the
 * same cards are cards.
 *
 * IT IS NO LONGER THE ONLY VIEW. Junaid asked for the marketplace's grid/row
 * choice here too, and he is right that the argument above settles the DEFAULT
 * and not the only way anyone ever wants to look at a shelf of card art. So
 * `ViewToggle` sits in the toolbar above 1024 and the grid is the alternative.
 * Below 1024 there is no toggle, because `Table` already mounts cards there —
 * see the note on `asGrid`.
 *
 * WHAT DIED: `GenericTable`, `TableFilters`, the hand-rolled skeleton, and the
 * `DropDown` overflow menu, which positioned itself from a ref captured on an
 * `<Ellipsis>` SVG with no button around it, so it could not be opened from the
 * keyboard at all. Its two commands are real controls in
 * `.dsk-table__rowactions` now, revealed on hover OR focus-within: a labelled
 * button for "Go to card" and a trash `IconButton` for remove, side by side on
 * one line. See the note on `rowActions` for why they used to stack.
 *
 * Removing a card from a collection is reversible (save it again from the card
 * page), so it takes no confirmation. The icon still carries the full label on
 * `aria-label`.
 */
export function SavedTableView({
  rows = [],
  loading = false,
  error,
  onRetry,

  search = '',
  onSearchChange,

  page = 1,
  pageCount = 1,
  pageSize = 10,
  total = 0,
  onPageChange,

  onOpenCard,
  onRemove,

  view = 'list',
  onViewChange,
  cardHref,
}) {
  /* THE TOGGLE IS A DESKTOP CONTROL, and not because grids are a desktop idea.
     `Table` already branches on `useViewport()` internally and mounts
     `.dsa-tablecards` — real cards — below 1024, because a table cannot be read
     on a phone. So on mobile the grid IS the only view, and shipping a control
     whose two positions do the same thing is worse than shipping none. Above
     1024 the table is genuine and the choice is genuine, which is where Junaid
     is: he described HOVERING a row. */
  const { isDesktop } = useViewport();
  const asGrid = isDesktop && view === 'grid';

  const columns = [
    {
      key: 'product_name',
      header: 'Card',
      render: (row) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
          <TableThumb src={mediaUrl(row.image_url)} alt="" />
          <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <span>{row.product_name}</span>
            {row.set_name ? <span className="dsk-hint">{row.set_name}</span> : null}
          </span>
        </span>
      ),
    },
    {
      key: 'rarity',
      header: 'Rarity',
      render: (row) => (row.rarity ? <Pill>{row.rarity}</Pill> : 'Not stated'),
    },
    {
      key: 'market_price',
      header: 'Market value',
      numeric: true,
      render: (row) => formatCurrency(row.market_price),
    },
  ];

  const emptyState = error ? (
    <ConsoleError error={error} onRetry={onRetry} title="Could not load your collection" />
  ) : (
    <ConsoleEmpty
      filtered={Boolean(search)}
      /* This wishlist is rendered on TWO surfaces: here, and as
         `SavedCardsSection` in the profile. They disagreed on the title
         and on the icon, which read as two different features. They say
         the same thing now, and the slug is the same scenario. */
      icon={Heart}
      illustration="no-saved-items-yet"
      title="Your collection is empty"
      text="Tap the heart on any card and it will be waiting for you here."
      filteredText="No saved card matches that search. Clear it to see your whole collection again."
      action={
        <Button as="a" href={routes.MARKETPLACE} variant="ghost" size="sm">
          Browse the marketplace
        </Button>
      }
      onClear={() => onSearchChange && onSearchChange('')}
    />
  );

  /* NO CONTROLS FOR NOTHING. An empty collection showed "Filter by card name",
     a "0 cards" count and a grid/table toggle stacked directly above "Your
     collection is empty" — a search box over nothing to search, and two views
     of the same void, as the first thing a new buyer sees on their dashboard.

     A search that MATCHED nothing keeps them: the box is the only place the
     query is visible, so hiding it would strand the buyer on an empty table
     with no way to see or clear what emptied it. Loading keeps them too, so
     the toolbar does not appear a beat after the rows it belongs to. */
  const trulyEmpty = !loading && !error && rows.length === 0 && !search;

  const toolbar = trulyEmpty ? null : (
    <Toolbar
      search={search}
      onSearchChange={onSearchChange}
      searchPlaceholder="Filter by card name"
      searchLabel="Filter your collection"
      count={loading ? undefined : `${total} ${total === 1 ? 'card' : 'cards'}`}
    >
      {isDesktop && onViewChange ? (
        <ViewToggle
          value={view === 'grid' ? 'grid' : 'list'}
          onChange={onViewChange}
          label="Collection view"
          gridLabel="Grid view"
          /* `ViewToggle`'s stock second label is "Rail view", which is the
             marketplace's horizontal rail. This surface's second view is the
             table, so the name has to say table or the control describes a
             thing that is not on the page. */
          listLabel="Table view"
        />
      ) : null}
    </Toolbar>
  );

  const pagination =
    pageCount > 1 ? (
      <Pagination
        page={page}
        pageCount={pageCount}
        pageSize={pageSize}
        totalItems={total}
        itemNoun="cards"
        onPageChange={onPageChange}
      />
    ) : null;

  if (asGrid) {
    /* `.dsk-table-wrap` and not a bespoke panel: it is the wrapper `Table`
       itself renders, so the toolbar hairline, the panel edge and the
       pagination rule are the same chrome in both views. Swapping the view
       must not also swap the frame around it. */
    return (
      <div className="dsk-table-wrap">
        {toolbar}
        {/* LOADING IS CHECKED BEFORE EMPTY, and that order is the bug this
            branch would otherwise ship. `Table` is handed `loading` and draws
            skeleton rows; a grid built by hand has to do the same work or the
            first paint of a real collection — rows still in flight, so `[]` —
            renders "Your collection is empty" and then replaces it with cards.
            `shape="tile"` is the 3:4 card stub, which is the shape a
            `ListingCard` tile actually is. Ten is the page size. */}
        {loading && rows.length === 0 ? (
          <ListingGrid columns={3} className="dsa-saved__grid" aria-busy="true">
            {Array.from({ length: pageSize }, (_, i) => (
              <Skeleton key={i} shape="tile" />
            ))}
          </ListingGrid>
        ) : error || rows.length === 0 ? (
          emptyState
        ) : (
          <ListingGrid columns={3} className="dsa-saved__grid">
            {rows.map((row) => (
              <ListingCard
                key={row.card_id}
                href={cardHref ? cardHref(row) : undefined}
                image={mediaUrl(row.image_url)}
                imageAlt=""
                set={row.set_name}
                name={row.product_name}
                rarity={row.rarity}
                price={formatCurrency(row.market_price)}
                /* Every card on this page is saved by definition, so the heart
                   is pressed on all of them and toggling it is the REMOVE
                   command — the grid's counterpart of the row's trash icon.
                   The label deliberately does not flip to "Remove": with
                   `aria-pressed` the name says what the control is for and the
                   state says whether it is on, per the note on `TileHeart`. */
                wishlisted
                wishlistLabel="Save to Collection"
                onWishlistToggle={() => onRemove && onRemove(row)}
              />
            ))}
          </ListingGrid>
        )}
        {pagination}
      </div>
    );
  }

  return (
    <Table
      columns={columns}
      data={error ? [] : rows}
      rowKey="card_id"
      loading={loading}
      onRowClick={(row) => onOpenCard && onOpenCard(row)}
      emptyState={emptyState}
      rowActions={(row) => (
        /* SIDE BY SIDE, AND THE WRAPPER IS WHY.
           The two commands used to be two `<>`-bare ghost buttons, and the
           actions column declares `style={{ width: 56 }}` on its `<th>`. Two
           inline-flex buttons carrying "Go to card" and "Remove from
           collection" cannot sit on one 56px line, so they wrapped — which is
           the stacking Junaid is describing, not a `flex-direction` anywhere.
           `nowrap` on a flex row is what actually holds them on one line; the
           column then takes the width it needs, since 56px is a hint and not a
           `table-layout: fixed` cap.

           Remove is an ICON and Go to card stays a labelled button. That is the
           split he asked for and it is also what makes one line fit: the long
           destructive label was most of the width. The icon keeps its name on
           `aria-label`, so nothing is lost to a screen reader.

           Removing is still reversible from the card page, so there is still no
           confirmation — see the note at the top of this file. */
        <span className="dsa-saved__rowactions">
          <Button
            variant="ghost"
            size="sm"
            onClick={(event) => {
              event.stopPropagation();
              onOpenCard(row);
            }}
          >
            Go to card
          </Button>
          <IconButton
            label={`Remove ${row.product_name || 'this card'} from collection`}
            onClick={(event) => {
              event.stopPropagation();
              onRemove(row);
            }}
          >
            <Icon as={Trash} />
          </IconButton>
        </span>
      )}
      toolbar={toolbar}
      pagination={pagination}
    />
  );
}

export default SavedTableView;
